import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Article } from "@/mocks/articles";
import { categoryLabels } from "@/mocks/articles";

interface Props {
  slides: Article[];
}

export default function HeroCarousel({ slides }: Props) {
  const [index, setIndex] = useState(0);
  const touchStart = useRef<number | null>(null);
  const total = slides.length;

  const goTo = useCallback(
    (next: number) => {
      setIndex((next + total) % total);
    },
    [total],
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setIndex((prev) => (prev + 1) % total);
    }, 5200);
    return () => window.clearInterval(timer);
  }, [total]);

  const active = slides[index];

  return (
    <section
      aria-label="焦点爆料轮播"
      className="relative w-full h-[340px] md:h-[440px] lg:h-full lg:min-h-[440px] rounded-lg overflow-hidden bg-primary-900"
    >
      {slides.map((slide, slideIndex) => (
        <Link
          key={slide.id}
          to={`/detail/${slide.id}`}
          className={`absolute inset-0 block cursor-pointer transition-opacity duration-700 ${
            slideIndex === index ? "opacity-100 z-10" : "opacity-0 z-0 pointer-events-none"
          }`}
          aria-hidden={slideIndex !== index}
          tabIndex={slideIndex === index ? 0 : -1}
          onTouchStart={(event) => {
            touchStart.current = event.touches[0].clientX;
          }}
          onTouchEnd={(event) => {
            if (touchStart.current === null) return;
            const delta = event.changedTouches[0].clientX - touchStart.current;
            if (Math.abs(delta) > 40) {
              goTo(delta > 0 ? index - 1 : index + 1);
            }
            touchStart.current = null;
          }}
        >
          <img
            src={slide.cover}
            alt={slide.title}
            title={`${slide.title} - 黑料吃瓜`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/55 to-primary-950/5" />
          <div className="absolute inset-x-0 bottom-0 p-4 md:p-7">
            <div className="flex items-center gap-2 mb-2.5">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded bg-accent-500 text-primary-950 text-[11px] font-bold whitespace-nowrap">
                <i className="ri-flashlight-fill" />
                今日焦点
              </span>
              <span className="px-2.5 py-1 rounded bg-background-50/15 text-background-100 text-[11px] backdrop-blur-sm whitespace-nowrap">
                {categoryLabels[slide.category]}
              </span>
            </div>
            <h2 className="font-heading text-lg md:text-[28px] font-black text-background-50 leading-snug line-clamp-2 max-w-3xl">
              {slide.title}
            </h2>
            <p className="hidden md:block text-[13px] text-background-200/85 mt-2 max-w-2xl line-clamp-2">
              {slide.excerpt}
            </p>
            <div className="flex items-center gap-3 mt-3 text-[12px] text-background-300/80">
              <span>{slide.date}</span>
              <span className="w-px h-3 bg-background-50/25" />
              <span>{slide.author}</span>
              <span className="w-px h-3 bg-background-50/25" />
              <span className="flex items-center gap-1">
                <i className="ri-eye-line" />
                {(slide.views / 10000).toFixed(1)} 万
              </span>
            </div>
          </div>
        </Link>
      ))}

      <div className="absolute z-20 right-3 md:right-5 top-1/2 -translate-y-1/2 flex flex-col gap-2">
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            goTo(index - 1);
          }}
          aria-label="上一条焦点"
          className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-primary-950/40 backdrop-blur-sm text-background-50 flex items-center justify-center cursor-pointer hover:bg-accent-500 hover:text-primary-950 transition-colors"
        >
          <i className="ri-arrow-up-s-line" />
        </button>
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            goTo(index + 1);
          }}
          aria-label="下一条焦点"
          className="w-8 h-8 md:w-9 md:h-9 rounded-full bg-primary-950/40 backdrop-blur-sm text-background-50 flex items-center justify-center cursor-pointer hover:bg-accent-500 hover:text-primary-950 transition-colors"
        >
          <i className="ri-arrow-down-s-line" />
        </button>
      </div>

      <div className="absolute z-20 bottom-4 right-4 md:bottom-7 md:right-7 flex items-center gap-1.5">
        {slides.map((slide, slideIndex) => (
          <button
            key={`dot-${slide.id}`}
            type="button"
            onClick={(event) => {
              event.preventDefault();
              goTo(slideIndex);
            }}
            aria-label={`切换到第 ${slideIndex + 1} 条`}
            className={`h-1.5 rounded-full transition-all cursor-pointer ${
              slideIndex === index ? "w-7 bg-accent-500" : "w-3 bg-background-50/40 hover:bg-background-50/70"
            }`}
          />
        ))}
      </div>

      <span className="sr-only">{active.title}</span>
    </section>
  );
}